// components/CouponTable.jsx
import React, { useMemo } from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  IconButton,
  Tooltip,
  Box,
  Chip,
  Switch,
  Stack,
  Typography,
} from "@mui/material";

const moneyBRL = (n) =>
  Number.isFinite(Number(n))
    ? new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(n))
    : "—";

const CouponTable = ({
  coupons = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading,
  onEditCoupon,
  onDeleteCoupon,
  onDeleteSelected,
  onToggleStatus, // opcional: se passar, o switch habilita
}) => {
  const rows = useMemo(
    () =>
      coupons.map((c) => {
        const tipo_label = c?.tipo === "percentual" ? "Percentual" : "Valor";
        const valor_label =
          c?.tipo === "percentual"
            ? `${Number(c?.valor || 0)}%`
            : c?.valor != null
            ? moneyBRL(c?.valor)
            : "—";

        const ini = c?.validade_inicio
          ? new Date(c.validade_inicio).toLocaleDateString("pt-BR")
          : "—";
        const fim = c?.validade_fim
          ? new Date(c.validade_fim).toLocaleDateString("pt-BR")
          : "—";
        const periodo_label = `${ini} → ${fim}`;

        const usos_label =
          c?.limite_usos_total != null
            ? `${c?.usos || 0}/${c?.limite_usos_total}`
            : "—";

        return {
          ...c,
          id: c.id,
          codigo: c.codigo || "—",
          tipo_label,
          valor_label,
          periodo_label,
          usos_label,
          status_label: Number(c?.status) === 1 ? "Ativo" : "Inativo",
        };
      }),
    [coupons]
  );

  const columns = [
    {
      field: "codigo",
      headerName: "Cupom",
      width: 360,
      renderCell: (p) => (
        <Stack spacing={0.3}>
          <Typography fontWeight={600} noWrap>
            {p.row.codigo}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.tipo_label} • {p.row.valor_label}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "periodo_label",
      headerName: "Período",
      width: 200,
      renderCell: (p) =>
        p.value && p.value !== "— → —" ? (
          <Chip size="small" label={p.value} />
        ) : (
          <Typography variant="body2">-</Typography>
        ),
    },
    {
      field: "usos_label",
      headerName: "Usos",
      width: 120,
      renderCell: (p) =>
        p.value && p.value !== "—" ? (
          <Chip size="small" label={p.value} />
        ) : (
          <Typography variant="body2">-</Typography>
        ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch
            size="small"
            checked={Number(params.row.status) === 1}
            onChange={() => onToggleStatus?.(params.row)}
            disabled={!onToggleStatus}
          />
          <Chip
            size="small"
            label={params.row.status_label}
            color={Number(params.row.status) === 1 ? "success" : "default"}
            variant="outlined"
          />
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 140,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => onEditCoupon?.(params.row)}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              onClick={() => onDeleteCoupon?.(params.row.id)}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle="Cupons Selecionados"
      exportFileName="cupons"
      onDeleteSelected={onDeleteSelected}
      searchPlaceholder="Buscar por código…"
      pageSizeOptions={[10, 25, 50, 100]}
      dense
    />
  );
};

export default CouponTable;
